import { PlusOutlined } from '@ant-design/icons'
import { GraviButton } from '@gravitate-js/excalibrr'
import { Button, Popover, Select } from 'antd'
import { debounce } from 'lodash'
import React, { useState, useCallback } from 'react'
import {
  getObjectType,
  useSearchCrossroadsEntities,
} from '../../../../api/hooks/useSearchCrossroadsEntities'
import { MappingSelectorProps } from '../types'
import { filterAvailableEntities } from '../utils/dataAlignment'

/**
 * Mapping selector component with search and filter functionality
 */
export const MappingSelector: React.FC<MappingSelectorProps> = ({
  tenantName,
  tenantEntityId,
  side,
  alignedRows,
  onSelect,
  entityType,
}) => {
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [selectedGoid, setSelectedGoid] = useState<string | undefined>(
    undefined,
  )
  const [searchTerm, setSearchTerm] = useState('')

  // Search for MRD entities
  const objectType = entityType ? getObjectType(entityType) : 'all'
  const { data: mrdEntities, isLoading } = useSearchCrossroadsEntities(
    searchTerm,
    objectType,
    popoverVisible, // Only enabled when popover is open
  )

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((term: string) => setSearchTerm(term), 300),
    [],
  )

  const handleSearch = useCallback(
    (value: string) => {
      debouncedSearch(value)
    },
    [debouncedSearch],
  )

  const handleSave = useCallback(() => {
    if (selectedGoid) {
      onSelect(selectedGoid)
      setPopoverVisible(false)
      setSelectedGoid(undefined)
      setSearchTerm('')
    }
  }, [selectedGoid, onSelect])

  const handleCancel = useCallback(() => {
    setPopoverVisible(false)
    setSelectedGoid(undefined)
    setSearchTerm('')
  }, [])

  // Filter out entities that already have mappings on the SAME side
  const filteredEntities = filterAvailableEntities(
    side,
    alignedRows,
    Array.isArray(mrdEntities) ? mrdEntities : [],
  )

  const options = filteredEntities.map((entity) => ({
    value: entity.goid,
    label: `${entity.name || entity.display_name} (${entity.goid})`,
  }))

  return (
    <Popover
      title='Select Master Data'
      trigger='click'
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
      overlayStyle={{ width: 300 }}
      content={
        <>
          <Select
            showSearch
            placeholder='Search for mapping'
            onSearch={handleSearch}
            onChange={(value) => setSelectedGoid(value)}
            loading={isLoading}
            filterOption={false}
            options={options}
            value={selectedGoid}
            style={{ width: '100%', marginBottom: 12 }}
          />
          <div style={{ textAlign: 'right' }}>
            <Button
              size='small'
              onClick={handleCancel}
              style={{ marginRight: 8 }}
            >
              Cancel
            </Button>
            <Button
              size='small'
              type='primary'
              onClick={handleSave}
              disabled={!selectedGoid}
            >
              OK
            </Button>
          </div>
        </>
      }
    >
      <GraviButton
        icon={<PlusOutlined />}
        type='primary'
        disabled={!tenantEntityId || tenantName === 'none'}
      >
        Map
      </GraviButton>
    </Popover>
  )
}
